import { useTranslation } from "react-i18next";
import {
  BulkActionCount,
  EfsMelcoModelsUserAlphabet,
  Search,
  useTableSelection,
} from "melco-shared-logic";
import {
  BulkActionContainerCol,
  Button,
  Col,
  PlusIcon,
  RemoveIcon,
  Table,
  TableActionBarRow,
  ValueOrFallback,
} from "melco-ui";
import { BulkActionAddFonts } from "./BulkActionAddFonts";
import { useAddFonts } from "../../../hooks/libraries/fontCollections/useAddFonts";
import { useFonts } from "../../../hooks/libraries/fonts/useFonts";

export const AddFontsList: React.FC = () => {
  const { addFonts, removeFonts, isAlreadyAdded } = useAddFonts();

  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
  } = useFonts("embroidery", "published");

  const { t } = useTranslation();

  const columns = [
    {
      title: t("fonts_list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
      render: (_: string, item: EfsMelcoModelsUserAlphabet) => (
        <ValueOrFallback
          value={item.name}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("fonts_list.columns.type"),
      key: "type",
      dataIndex: "type",
      sorter: true,
    },
    {
      title: "",
      key: "action",
      dataIndex: "action",
      align: "right" as const,
      render: (_: string, font: EfsMelcoModelsUserAlphabet) => {
        return isAlreadyAdded(font) ? (
          <Button
            icon={<RemoveIcon />}
            size="small"
            onClick={() => removeFonts([font])}
          >
            {t("font_collections.actions.add_fonts.actions.remove")}
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<PlusIcon />}
            size="small"
            onClick={() => addFonts([font])}
          >
            {t("global.button.add")}
          </Button>
        );
      },
    },
  ];

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection<EfsMelcoModelsUserAlphabet>(() => {});

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionAddFonts
            allFonts={data ?? []}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        rowSelection={rowSelection}
        onRow={onRow}
        noDataMessage={t("fonts_list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
